import { useEffect, useRef } from 'react'

import useApiGet from 'Pods/Shared/Hooks/useApiGet'

import { publishGenerationStatus, setGenerationResult } from './actions'
import { buildLastResult } from './buildLastResult'
import { generationStatusStore } from './index'

const STUDIO_MOUNT_ID = 'three-d-studio-elements'

// On /ai the studio drives the store directly (and mirrors into it), so this watcher
// stays out of the way to avoid a duplicate subscription.
function isStudioPresent() {
  return typeof document !== 'undefined' && !!document.getElementById(STUDIO_MOUNT_ID)
}

function toTimestamp(value) {
  if (!value) {
    return null
  }
  const ms = new Date(value).getTime()
  return Number.isNaN(ms) ? null : ms
}

function ensureCableConsumer() {
  if (typeof window === 'undefined') {
    return null
  }
  window.App = window.App || {}
  if (!window.App.cable?.subscriptions) {
    // eslint-disable-next-line global-require
    const ActionCable = require('actioncable')
    window.App.cable = ActionCable.createConsumer()
  }
  return window.App.cable
}

// Off-/ai watcher for the global generation-status store. /ai is the sole producer;
// this hook does NOT start generations. It runs from the top nav (mounted on every
// page) so a run started in /ai still resolves to succeeded/failed when the user has
// navigated away or is on another tab - the /ai subscription dies with that page, so
// without this nothing off /ai would catch the completion broadcast.
//
// To keep page loads cheap it only touches the backend when THIS browser already
// believes a run is in flight (persisted to localStorage by /ai); an idle store does
// nothing. It re-subscribes to a still-pending run, or reconciles one that finished
// while no page was mounted via /runs/:id.
//
// It reconciles both at mount and whenever the store transitions idle -> generating
// afterwards: another tab starting a run writes localStorage, which rehydrates this
// tab's store via the cross-tab `storage` listener (see index.js). Without reacting to
// that, an already-open tab would never subscribe and could stay stuck in-flight if the
// /ai tab closed before the terminal broadcast.
export default function useGenerationStatusWatcher({ isCurrentUser }) {
  const { getData } = useApiGet()
  const subscriptionRef = useRef(null)
  // useApiGet's getData is recreated whenever Sentry finishes loading (its useSentry
  // dep flips), so keep it in a ref to keep it out of the effect deps - otherwise the
  // effect tears down and re-subscribes the live run channel mid-generation, risking a
  // missed completion broadcast. Mirrors navigate/location refs in useGeneratorRunChannel.
  const getDataRef = useRef(getData)
  getDataRef.current = getData

  useEffect(() => {
    if (!isCurrentUser || isStudioPresent()) {
      return undefined
    }

    // Guards re-entrancy: reconcile() re-publishes status (an idle -> generating store
    // write), which re-fires our own store listener. Only the first transition into a
    // generating state should hit the backend; it resets when the run settles.
    let reconciling = false

    function cleanup() {
      if (subscriptionRef.current) {
        subscriptionRef.current.unsubscribe()
        subscriptionRef.current = null
      }
    }

    function subscribe(runId) {
      const cable = ensureCableConsumer()
      if (!cable?.subscriptions || !runId) {
        return
      }
      cleanup()
      subscriptionRef.current = cable.subscriptions.create(
        { channel: 'GeneratorRunsChannel', run_id: runId },
        {
          rejected() {
            publishGenerationStatus({ isGenerating: false, runId: undefined })
            cleanup()
          },
          received(message) {
            if (message?.type !== 'generator/runs/update') {
              return
            }
            setGenerationResult(buildLastResult(message?.data?.attributes || {}))
            publishGenerationStatus({ isGenerating: false, runId: undefined })
            cleanup()
          },
        }
      )
    }

    function resolveFinishedRun(runId) {
      getDataRef.current({
        path: `/api/internal/generator/runs/${runId}`,
        onSuccess: (response) => {
          setGenerationResult(buildLastResult(response.data?.data?.attributes || {}))
          publishGenerationStatus({ isGenerating: false, runId: undefined })
        },
        onError: () => publishGenerationStatus({ isGenerating: false, runId: undefined }),
      })
    }

    // Reconcile the hydrated (possibly stale) in-flight state against the backend.
    function reconcile() {
      const { runId: knownRunId } = generationStatusStore.getState()
      getDataRef.current({
        path: '/api/internal/generator/runs/pending',
        onSuccess: (response) => {
          const first = response.data?.data?.[0]
          const pendingRun = first?.runs?.[0]

          if (pendingRun?.id != null) {
            publishGenerationStatus({
              isGenerating: true,
              runId: pendingRun.id,
              sessionId: first.session,
              generationStartedAt: toTimestamp(pendingRun.created_at),
            })
            subscribe(pendingRun.id)
            return
          }

          // Nothing in flight: the run finished while no page was mounted to catch the
          // broadcast. Resolve it from its final state.
          if (knownRunId != null) {
            resolveFinishedRun(knownRunId)
          } else {
            publishGenerationStatus({ isGenerating: false, runId: undefined })
          }
        },
        onError: () => publishGenerationStatus({ isGenerating: false, runId: undefined }),
      })
    }

    function maybeReconcile() {
      const { isGenerating } = generationStatusStore.getState()
      if (isGenerating && !reconciling) {
        reconciling = true
        reconcile()
      } else if (!isGenerating) {
        reconciling = false
      }
    }

    maybeReconcile()
    const unsubscribeStore = generationStatusStore.subscribe(maybeReconcile)

    return () => {
      unsubscribeStore()
      cleanup()
    }
  }, [isCurrentUser])
}
